import { supabase } from './supabaseClient';
import { getLocalChapter } from '../data/localChapterData';
import { isSubscriptionTestModeEnabled } from './testingMode';

const FUNCTION_URL = process.env.REACT_APP_SUPABASE_URL + '/functions/v1/get-chapter';

// Cache for loaded chapters
const chapterCache = new Map();

/**
 * Fetch chapter data from the server
 * @param {string} courseId - The course ID (e.g., 'binary-search')
 * @returns {Promise<object>} - The chapter data
 */
export async function fetchChapter(courseId) {
  // Check cache first
  if (chapterCache.has(courseId)) {
    return chapterCache.get(courseId);
  }

  if (isSubscriptionTestModeEnabled()) {
    const localChapter = await getLocalChapter(courseId);
    if (localChapter) {
      chapterCache.set(courseId, localChapter);
      return localChapter;
    }
  }

  // Get current session for auth header
  const { data: { session } } = await supabase.auth.getSession();

  const headers = {
    'Content-Type': 'application/json',
  };

  // Add auth header if user is logged in
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }

  const response = await fetch(`${FUNCTION_URL}?courseId=${encodeURIComponent(courseId)}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to fetch chapter: ${response.status}`);
  }

  const data = await response.json();

  // Cache the result
  chapterCache.set(courseId, data);

  return data;
}

/**
 * Clear the chapter cache (useful after login/logout)
 */
export function clearChapterCache() {
  chapterCache.clear();
}

/**
 * Preload multiple chapters
 * @param {string[]} courseIds - Array of course IDs to preload
 */
export async function preloadChapters(courseIds) {
  await Promise.allSettled(courseIds.map(id => fetchChapter(id)));
}
